import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  getAuth, 
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  createUserWithEmailAndPassword
} from 'firebase/auth';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const [isOAuthCallback, setIsOAuthCallback] = useState(false);
  const auth = getAuth();

  function signup(email, password) {
    return createUserWithEmailAndPassword(auth, email, password);
  }

  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  async function logout() {
    try {
      await signOut(auth);
      // Clear any local storage items that might be related to user session
      localStorage.removeItem('darkMode');

      localStorage.removeItem('roadmapTemplates');
      localStorage.removeItem('clockedIn');
      localStorage.removeItem('onBreak');
      localStorage.removeItem('currentJob');
      localStorage.removeItem('startTime');
      localStorage.removeItem('breakStartTime');
      localStorage.removeItem('notes');
      // The redirect will be handled by the auth state change listener
      // which will trigger SmartRedirect to redirect to /login
    } catch (error) {
      console.error('Error during logout:', error);
      throw error;
    }
  }

  useEffect(() => {
    let timeoutId;
    let loadingTimeoutId;
    let unsubscribe;

    // Check if we're on an OAuth callback page
    const checkOAuthCallback = () => {
      const path = window.location.pathname;
      return path.includes('/auth/') && path.includes('/callback');
    };

    const setupAuthListener = () => {
      unsubscribe = onAuthStateChanged(auth, (user) => {
        setCurrentUser(user);
        setLoading(false);
        setAuthError(null);
        setLoadingTimeout(false);
        setIsOAuthCallback(false);
        
        // Clear the timeout since auth state resolved
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        if (loadingTimeoutId) {
          clearTimeout(loadingTimeoutId);
          loadingTimeoutId = null;
        }
      }, (error) => {
        console.error('Auth state change error:', error);
        setAuthError(error);
        setLoading(false);
        setCurrentUser(null);
        setLoadingTimeout(false);
        setIsOAuthCallback(false);
        
        // Clear the timeout since we got an error
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        if (loadingTimeoutId) {
          clearTimeout(loadingTimeoutId);
          loadingTimeoutId = null;
        }
      });
    };

    // Check if we're on OAuth callback page
    const isOAuthPage = checkOAuthCallback();
    console.log('OAuth callback check:', {
      pathname: window.location.pathname,
      isOAuthPage,
      search: window.location.search
    });
    
    if (isOAuthPage) {
      setIsOAuthCallback(true);
    }

    // Set a timeout to prevent infinite loading (longer for OAuth callbacks)
    const timeoutDuration = isOAuthCallback ? 30000 : 10000; // 30 seconds for OAuth, 10 for normal
    timeoutId = setTimeout(() => {
      if (isOAuthCallback) {
        console.warn('OAuth callback timeout - but not redirecting to prevent logout');
        return; // Don't redirect during OAuth callback
      }
      console.warn('Auth state change timeout - redirecting to login');
      setLoading(false);
      setCurrentUser(null);
      setAuthError(new Error('Authentication timeout'));
      setLoadingTimeout(false);
      
      // Force redirect to login after timeout
      window.location.href = '/login';
    }, timeoutDuration);

    // Show loading timeout indicator after 5 seconds
    loadingTimeoutId = setTimeout(() => {
      setLoadingTimeout(true);
    }, 5000);

    setupAuthListener();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (loadingTimeoutId) {
        clearTimeout(loadingTimeoutId);
      }
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [auth]);

  const value = {
    currentUser,
    loading,
    loadingTimeout,
    authError,
    isOAuthCallback,
    signup,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 